import { useRef, useState, useEffect, useCallback } from 'react'

interface VideoProgressBarProps {
  progress: number
  variant?: 'modal' | 'compact'
  currentTime?: number
  duration?: number
  onSeek?: (percent: number) => void
}

function formatTime(time: number): string {
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

const VideoProgressBar = ({
  progress,
  variant = 'compact',
  currentTime = 0,
  duration = 0,
  onSeek,
}: VideoProgressBarProps) => {
  const progressRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleSeek = useCallback((clientX: number) => {
    if (!progressRef.current || !onSeek) return
    const rect = progressRef.current.getBoundingClientRect()
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    onSeek(percent * 100)
  }, [onSeek])

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    handleSeek(e.clientX)
  }

  const handleMouseDown = () => {
    if (onSeek) setIsDragging(true)
  }

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      handleSeek(e.clientX)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleSeek])

  if (variant === 'compact') {
    return (
      <div className="flex-1 h-[4.3px] sm:h-2 bg-[#959595] rounded-full overflow-hidden">
        <div
          className="h-full bg-white rounded-full transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>
    )
  }

  return (
    <div className="w-full">
      <div
        ref={progressRef}
        className="relative h-1 bg-white/30 rounded-full cursor-pointer mb-2"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      >
        <div
          className="absolute h-full bg-white rounded-full transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md transition-all duration-100"
          style={{ left: `calc(${progress}% - 6px)` }}
        />
      </div>
      <div className="flex justify-end">
        <span className="text-white text-sm font-medium">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
    </div>
  )
}

export default VideoProgressBar
